import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analytics.service';
import { KpiQueryParams, MonthlyCompanyHealthScore } from '../../types/kpi.types';
import { LineChart } from '../Charts';
import { Loader } from '../Loader';

interface CompanyTransferTotal {
  companyId: number;
  companyName: string;
  totalTransfers: number;
  totalAmount: number;
}

interface CompanyUserStatus {
  companyId: number;
  companyName: string;
  active: number;
  notActive: number;
  blocked: number;
  left: number;
  total: number;
}

interface ClientOverviewProps {
  queryParams: KpiQueryParams;
}

const formatMonth = (month: string): string => {
  const [year, m] = month.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m) - 1]} '${year.slice(2)}`;
};

const formatAmount = (amount: number): string =>
  '₪' + Math.round(amount).toLocaleString();

const getHealthStatus = (score: number): { label: string; color: string; emoji: string } => {
  if (score >= 70) return { label: 'Healthy', color: '#00C851', emoji: '🟢' };
  if (score >= 40) return { label: 'Needs Attention', color: '#ffbb33', emoji: '🟡' };
  return { label: 'Critical', color: '#ff4444', emoji: '🔴' };
};

export const ClientOverview: React.FC<ClientOverviewProps> = ({
  queryParams,
}) => {
  const [monthlyHealthData, setMonthlyHealthData] = useState<MonthlyCompanyHealthScore[]>([]);
  const [companyTotals, setCompanyTotals] = useState<CompanyTransferTotal[]>([]);
  const [userStatusData, setUserStatusData] = useState<CompanyUserStatus[]>([]);
  const [statusSortCol, setStatusSortCol] = useState<'companyName' | 'total' | 'active' | 'notActive' | 'blocked'>('notActive');
  const [statusSortDir, setStatusSortDir] = useState<'asc' | 'desc'>('desc');
  const [sortCol, setSortCol] = useState<'companyName' | 'totalTransfers' | 'totalAmount' | 'avg'>('totalTransfers');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [healthData, totals, userStatus] = await Promise.all([
          analyticsService.getMonthlyCompanyHealthScores(queryParams),
          analyticsService.getCompanyTransferTotals(queryParams.database),
          analyticsService.getUserStatusByCompany(queryParams.database),
        ]);
        setMonthlyHealthData(healthData);
        setCompanyTotals(totals);
        setUserStatusData(userStatus);
      } catch (err: any) {
        console.error('Error loading client overview:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [queryParams.startDate, queryParams.endDate, queryParams.database, queryParams.companyId]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#ff4444', backgroundColor: '#fff', borderRadius: '12px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <div style={{ fontSize: '18px', fontWeight: 600 }}>Error Loading Data</div>
        <div style={{ fontSize: '14px', marginTop: '8px' }}>{error}</div>
      </div>
    );
  }

  // Get unique companies
  const companies = Array.from(new Set(monthlyHealthData.map(d => ({ id: d.companyId, name: d.companyName }))))
    .map((c, idx, arr) => arr.find(x => x.id === c.id && x.name === c.name) || c)
    .filter((c, idx, arr) => arr.findIndex(x => x.id === c.id) === idx)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Filter data by selected company (from global selector)
  const filteredData = queryParams.companyId
    ? monthlyHealthData.filter(d => d.companyId.toString() === queryParams.companyId)
    : monthlyHealthData;

  // Group by company for trend charts
  const companyTrendData = companies.map(company => {
    const companyData = monthlyHealthData.filter(d => d.companyId === company.id);
    return {
      companyId: company.id,
      companyName: company.name,
      trend: companyData
        .sort((a, b) => a.month.localeCompare(b.month))
        .map(d => ({
          date: d.month,
          value: d.healthScore,
          companyId: d.companyId.toString(),
          companyName: d.companyName,
        })),
    };
  });

  // Calculate monthly totals (aggregate across all companies)
  const monthlyTotalsMap = new Map<string, {
    month: string;
    totalEmployeesWithShifts: number;
    totalEmployeesWithTransfers: number;
    totalMau: number;
    totalRepeatUsers: number;
    totalChurned: number;
    avgHealthScore: number;
    avgAdoptionRate: number;
    avgRetentionRate: number;
    avgLoyaltyRate: number;
  }>();

  filteredData.forEach(row => {
    if (!monthlyTotalsMap.has(row.month)) {
      monthlyTotalsMap.set(row.month, {
        month: row.month,
        totalEmployeesWithShifts: 0,
        totalEmployeesWithTransfers: 0,
        totalMau: 0,
        totalRepeatUsers: 0,
        totalChurned: 0,
        avgHealthScore: 0,
        avgAdoptionRate: 0,
        avgRetentionRate: 0,
        avgLoyaltyRate: 0,
      });
    }
    const totals = monthlyTotalsMap.get(row.month)!;
    totals.totalEmployeesWithShifts += row.totalEmployeesWithShifts;
    totals.totalEmployeesWithTransfers += row.employeesWithTransfers;
    totals.totalMau += row.mau;
    totals.totalRepeatUsers += row.repeatUsers;
    totals.totalChurned += row.churnedEmployees;
  });

  // Calculate averages
  const monthlyTotals = Array.from(monthlyTotalsMap.values())
    .map(totals => {
      const companyCount = filteredData.filter(d => d.month === totals.month).length;
      const avgHealthScore = companyCount > 0
        ? filteredData
            .filter(d => d.month === totals.month)
            .reduce((sum, d) => sum + d.healthScore, 0) / companyCount
        : 0;
      const avgAdoptionRate = totals.totalEmployeesWithShifts > 0
        ? (totals.totalEmployeesWithTransfers / totals.totalEmployeesWithShifts) * 100
        : 0;
      const avgRetentionRate = totals.totalEmployeesWithShifts > 0
        ? ((totals.totalEmployeesWithShifts - totals.totalChurned) / totals.totalEmployeesWithShifts) * 100
        : 100;
      const avgLoyaltyRate = totals.totalMau > 0
        ? (totals.totalRepeatUsers / totals.totalMau) * 100
        : 0;

      return {
        ...totals,
        avgHealthScore: Math.round(avgHealthScore * 100) / 100,
        avgAdoptionRate: Math.round(avgAdoptionRate * 100) / 100,
        avgRetentionRate: Math.round(avgRetentionRate * 100) / 100,
        avgLoyaltyRate: Math.round(avgLoyaltyRate * 100) / 100,
      };
    })
    .sort((a, b) => a.month.localeCompare(b.month));

  // Display data: if "all" show totals, otherwise show company-specific
  const displayData = !queryParams.companyId
    ? monthlyTotals.map(t => ({
        month: t.month,
        companyId: 0,
        companyName: 'All Companies',
        healthScore: t.avgHealthScore,
        adoptionRate: t.avgAdoptionRate,
        retentionRate: t.avgRetentionRate,
        loyaltyRate: t.avgLoyaltyRate,
        totalEmployeesWithShifts: t.totalEmployeesWithShifts,
        employeesWithTransfers: t.totalEmployeesWithTransfers,
        mau: t.totalMau,
        repeatUsers: t.totalRepeatUsers,
        churnedEmployees: t.totalChurned,
      }))
    : filteredData.sort((a, b) => a.month.localeCompare(b.month));

  const displayLabel = !queryParams.companyId
    ? 'All Companies (Average)'
    : companies.find(c => c.id.toString() === queryParams.companyId)?.name || 'Company';

  const selectedCompanyTrend = !queryParams.companyId
    ? monthlyTotals.map(t => ({
        date: t.month,
        value: t.avgHealthScore,
      }))
    : companyTrendData.find(c => c.companyId.toString() === queryParams.companyId)?.trend || [];

  const handleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const arrow = (col: typeof sortCol) =>
    sortCol === col ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ' ↕';

  const handleStatusSort = (col: typeof statusSortCol) => {
    if (statusSortCol === col) setStatusSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setStatusSortCol(col); setStatusSortDir('desc'); }
  };

  const statusArrow = (col: typeof statusSortCol) =>
    statusSortCol === col ? (statusSortDir === 'desc' ? ' ↓' : ' ↑') : ' ↕';

  const filteredUserStatus = queryParams.companyId
    ? userStatusData.filter(c => c.companyId.toString() === queryParams.companyId)
    : userStatusData;

  const displayedUserStatus = [...filteredUserStatus].sort((a, b) => {
    const aZero = a.total === 0;
    const bZero = b.total === 0;
    if (aZero && !bZero) return 1;
    if (!aZero && bZero) return -1;
    const mul = statusSortDir === 'asc' ? 1 : -1;
    if (statusSortCol === 'companyName') return mul * a.companyName.localeCompare(b.companyName);
    return mul * ((a[statusSortCol] as number) - (b[statusSortCol] as number));
  });

  // Filter company totals by selected company
  const filtered = queryParams.companyId
    ? companyTotals.filter(c => c.companyId.toString() === queryParams.companyId)
    : companyTotals;

  const displayedTotals = [...filtered].sort((a, b) => {
    const aZero = a.totalTransfers === 0 && a.totalAmount === 0;
    const bZero = b.totalTransfers === 0 && b.totalAmount === 0;
    if (aZero && !bZero) return 1;
    if (!aZero && bZero) return -1;
    const mul = sortDir === 'asc' ? 1 : -1;
    if (sortCol === 'companyName') return mul * a.companyName.localeCompare(b.companyName);
    if (sortCol === 'totalTransfers') return mul * (a.totalTransfers - b.totalTransfers);
    if (sortCol === 'totalAmount') return mul * (a.totalAmount - b.totalAmount);
    const avgA = a.totalTransfers > 0 ? a.totalAmount / a.totalTransfers : 0;
    const avgB = b.totalTransfers > 0 ? b.totalAmount / b.totalTransfers : 0;
    return mul * (avgA - avgB);
  });

  const grandTotalTransfers = displayedTotals.reduce((sum, c) => sum + c.totalTransfers, 0);
  const grandTotalAmount = displayedTotals.reduce((sum, c) => sum + c.totalAmount, 0);

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#333' }}>
          📊 Monthly Health Score Overview
        </h2>
        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
          Track company health scores over time • Adoption (35%) + Retention (35%) + Loyalty (30%)
        </p>
      </div>

      {/* All-time transfers per company */}
      {displayedTotals.length > 0 && (
        <div style={{
          backgroundColor: '#fff',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          marginBottom: '24px',
        }}>
          <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            💸 All-Time Successful Transfers per Company
          </h3>
          <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#888' }}>
            Cumulative totals since launch — successful transfers only (status 500)
          </p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <table style={{ width: '700px', borderCollapse: 'collapse', fontSize: '14px', direction: 'rtl' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0', color: '#aaa', width: '40px' }}>#</th>
                  <th onClick={() => handleSort('companyName')} style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                    חברה{arrow('companyName')}
                  </th>
                  <th onClick={() => handleSort('totalTransfers')} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                    סה״כ העברות{arrow('totalTransfers')}
                  </th>
                  <th onClick={() => handleSort('totalAmount')} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                    סה״כ סכום{arrow('totalAmount')}
                  </th>
                  <th onClick={() => handleSort('avg')} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                    ממוצע להעברה{arrow('avg')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedTotals.map((c, idx) => (
                  <tr key={c.companyId} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', color: '#aaa', fontSize: '13px', textAlign: 'right' }}>
                      {idx + 1}
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontWeight: 500, textAlign: 'right' }}>
                      {c.companyName}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #eee', fontWeight: 600 }}>
                      {c.totalTransfers.toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #eee', color: '#2E7D32', fontWeight: 500 }}>
                      {formatAmount(c.totalAmount)}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #eee', color: '#666' }}>
                      {c.totalTransfers > 0 ? formatAmount(c.totalAmount / c.totalTransfers) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#f0f4ff', fontWeight: 700 }}>
                  <td style={{ borderTop: '2px solid #c0c0c0' }} />
                  <td style={{ padding: '10px 12px', borderTop: '2px solid #c0c0c0', textAlign: 'right' }}>
                    סה״כ
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'left', borderTop: '2px solid #c0c0c0' }}>
                    {grandTotalTransfers.toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'left', borderTop: '2px solid #c0c0c0', color: '#2E7D32' }}>
                    {formatAmount(grandTotalAmount)}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'left', borderTop: '2px solid #c0c0c0', color: '#666' }}>
                    {grandTotalTransfers > 0 ? formatAmount(grandTotalAmount / grandTotalTransfers) : '—'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* User Status by Company */}
      {displayedUserStatus.length > 0 && (
        <div style={{
          backgroundColor: '#fff',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          marginBottom: '24px',
        }}>
          <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            👥 User Status by Company
          </h3>
          <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#888' }}>
            Employees who never logged in (Not Active) represent an untapped activation opportunity
          </p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <table style={{ width: '800px', borderCollapse: 'collapse', fontSize: '14px', direction: 'rtl' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0', color: '#aaa', width: '40px' }}>#</th>
                  <th onClick={() => handleStatusSort('companyName')} style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                    חברה{statusArrow('companyName')}
                  </th>
                  <th onClick={() => handleStatusSort('total')} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                    סה״כ{statusArrow('total')}
                  </th>
                  <th onClick={() => handleStatusSort('active')} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', color: '#2E7D32' }}>
                    פעיל{statusArrow('active')}
                  </th>
                  <th onClick={() => handleStatusSort('notActive')} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', color: '#E65100' }}>
                    לא פעיל{statusArrow('notActive')}
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#E65100' }}>
                    % לא פעיל
                  </th>
                  <th onClick={() => handleStatusSort('blocked')} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', color: '#c62828' }}>
                    חסום{statusArrow('blocked')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedUserStatus.map((c, idx) => {
                  const notActivePct = c.total > 0 ? Math.round((c.notActive / c.total) * 100) : 0;
                  return (
                    <tr key={c.companyId} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', color: '#aaa', fontSize: '13px', textAlign: 'right' }}>
                        {idx + 1}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontWeight: 500, textAlign: 'right' }}>
                        {c.companyName}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #eee', color: '#333', fontWeight: 600 }}>
                        {c.total.toLocaleString()}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #eee', color: '#2E7D32', fontWeight: 500 }}>
                        {c.active.toLocaleString()}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #eee', color: '#E65100', fontWeight: 600 }}>
                        {c.notActive.toLocaleString()}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #eee' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ flex: 1, height: '8px', backgroundColor: '#eee', borderRadius: '4px', minWidth: '60px' }}>
                            <div style={{ width: `${notActivePct}%`, height: '100%', backgroundColor: notActivePct > 50 ? '#E65100' : notActivePct > 25 ? '#FFA726' : '#66BB6A', borderRadius: '4px' }} />
                          </div>
                          <span style={{ fontSize: '12px', color: notActivePct > 50 ? '#E65100' : '#666', fontWeight: notActivePct > 50 ? 700 : 400, minWidth: '32px' }}>
                            {notActivePct}%
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #eee', color: c.blocked > 0 ? '#c62828' : '#aaa' }}>
                        {c.blocked > 0 ? c.blocked.toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#f0f4ff', fontWeight: 700 }}>
                  <td style={{ borderTop: '2px solid #c0c0c0' }} />
                  <td style={{ padding: '10px 12px', borderTop: '2px solid #c0c0c0', textAlign: 'right' }}>סה״כ</td>
                  <td style={{ padding: '10px 12px', textAlign: 'left', borderTop: '2px solid #c0c0c0' }}>
                    {displayedUserStatus.reduce((s, c) => s + c.total, 0).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'left', borderTop: '2px solid #c0c0c0', color: '#2E7D32' }}>
                    {displayedUserStatus.reduce((s, c) => s + c.active, 0).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'left', borderTop: '2px solid #c0c0c0', color: '#E65100' }}>
                    {displayedUserStatus.reduce((s, c) => s + c.notActive, 0).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'left', borderTop: '2px solid #c0c0c0' }}>
                    {(() => {
                      const t = displayedUserStatus.reduce((s, c) => s + c.total, 0);
                      const na = displayedUserStatus.reduce((s, c) => s + c.notActive, 0);
                      return t > 0 ? `${Math.round((na / t) * 100)}%` : '—';
                    })()}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'left', borderTop: '2px solid #c0c0c0', color: '#c62828' }}>
                    {displayedUserStatus.reduce((s, c) => s + c.blocked, 0).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Health Score Trend Chart */}
      {selectedCompanyTrend.length > 0 && (
        <div style={{
          backgroundColor: '#fff',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          marginBottom: '24px',
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            📈 Health Score Trend - {displayLabel}
          </h3>
          <LineChart
            data={selectedCompanyTrend}
            color="#4CAF50"
            height={300}
          />
        </div>
      )}

      {/* Monthly Health Scores Table */}
      <div style={{
        backgroundColor: '#fff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
          📅 Monthly Health Scores - {displayLabel}
        </h3>
        
        {displayData.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
            No monthly health score data available
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Month</th>
                  {!queryParams.companyId && (
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Companies</th>
                  )}
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>Health Score</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>Adoption (35%)</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>Retention (35%)</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>Loyalty (30%)</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>Employees w/ Shifts</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>Used Payro</th>
                </tr>
              </thead>
              <tbody>
                {displayData.map((row, idx) => {
                  const status = getHealthStatus(row.healthScore);
                  return (
                    <tr key={`${row.month}-${row.companyId}`} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee', fontWeight: 500 }}>
                        {formatMonth(row.month)}
                      </td>
                      {!queryParams.companyId && (
                        <td style={{ padding: '12px', borderBottom: '1px solid #eee', color: '#666' }}>
                          {monthlyHealthData.filter(d => d.month === row.month).length} companies
                        </td>
                      )}
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                          <span style={{ fontSize: '16px', fontWeight: 700, color: status.color }}>
                            {row.healthScore.toFixed(1)}
                          </span>
                          <span style={{ fontSize: '12px' }}>{status.emoji}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                        <div style={{ fontSize: '13px', color: '#333', fontWeight: 500 }}>
                          {row.adoptionRate.toFixed(1)}%
                        </div>
                        <div style={{ fontSize: '11px', color: '#888' }}>
                          {row.employeesWithTransfers} / {row.totalEmployeesWithShifts}
                        </div>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                        <div style={{ fontSize: '13px', color: '#333', fontWeight: 500 }}>
                          {row.retentionRate.toFixed(1)}%
                        </div>
                        <div style={{ fontSize: '11px', color: '#888' }}>
                          {row.churnedEmployees} Payro users
                        </div>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                        <div style={{ fontSize: '13px', color: '#333', fontWeight: 500 }}>
                          {row.loyaltyRate.toFixed(1)}%
                        </div>
                        <div style={{ fontSize: '11px', color: '#888' }}>
                          {row.repeatUsers} / {row.mau} MAU
                        </div>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                        {row.totalEmployeesWithShifts.toLocaleString()}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee', color: '#4CAF50', fontWeight: 500 }}>
                        {row.employeesWithTransfers.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* All Companies Trend Chart Grid */}
      {!queryParams.companyId && companyTrendData.length > 0 && (
        <div style={{
          backgroundColor: '#fff',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          marginTop: '24px',
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            📊 Health Score Trends by Company
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
            gap: '24px',
          }}>
            {companyTrendData.slice(0, 12).map(company => (
              <div key={company.companyId} style={{ cursor: 'default' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#333' }}>
                  {company.companyName}
                </div>
                <LineChart
                  data={company.trend}
                  color="#4CAF50"
                  height={150}
                />
              </div>
            ))}
          </div>
          {companyTrendData.length > 12 && (
            <div style={{ marginTop: '16px', textAlign: 'center', color: '#888', fontSize: '13px' }}>
              Showing first 12 companies. Select a company from the dropdown above to see more details.
            </div>
          )}
        </div>
      )}

      {/* Comprehensive Explanation */}
      <div style={{
        marginTop: '24px',
        backgroundColor: '#fff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
          📖 Client Overview Guide
        </h3>
        
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600, color: '#333' }}>
            What is the Monthly Health Score?
          </h4>
          <p style={{ margin: 0, fontSize: '14px', color: '#555', lineHeight: '1.6' }}>
            The <strong>Monthly Health Score</strong> is a composite metric (0-100) that measures how well each company is using Payro. 
            It's calculated monthly using three key components, each weighted differently:
          </p>
          <div style={{
            marginTop: '12px',
            padding: '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            borderLeft: '4px solid #4CAF50',
          }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#333' }}>
              Health Score = (Adoption × 35%) + (Retention × 35%) + (Loyalty × 30%)
            </div>
            <div style={{ fontSize: '13px', color: '#666' }}>
              Scores are color-coded: 🟢 70+ (Healthy), 🟡 40-69 (Needs Attention), 🔴 Below 40 (Critical)
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          {/* Adoption */}
          <div style={{
            padding: '16px',
            backgroundColor: '#E3F2FD',
            borderRadius: '8px',
            border: '1px solid #90CAF9',
          }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#1565C0' }}>
              📈 Adoption (35% weight)
            </div>
            <div style={{ fontSize: '14px', color: '#424242', lineHeight: '1.6', marginBottom: '8px' }}>
              <strong>Definition:</strong> The percentage of employees who had shifts in that month and made at least one successful transfer using Payro.
            </div>
            <div style={{ fontSize: '13px', color: '#555', backgroundColor: '#fff', padding: '8px', borderRadius: '4px' }}>
              <strong>Calculation:</strong> (Employees with shifts who used Payro) ÷ (Total employees with shifts) × 100
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
              Example: 121 employees used Payro out of 421 employees with shifts = 28.7% adoption
            </div>
          </div>

          {/* Retention */}
          <div style={{
            padding: '16px',
            backgroundColor: '#FFF3E0',
            borderRadius: '8px',
            border: '1px solid #FFB74D',
          }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#E65100' }}>
              🔄 Retention (35% weight)
            </div>
            <div style={{ fontSize: '14px', color: '#424242', lineHeight: '1.6', marginBottom: '8px' }}>
              <strong>Definition:</strong> Measures how long Payro users remain active relative to their potential lifetime. Calculated as the ratio of Payro user months (months users were active Payro users) to their potential months (minimum of registered months or months until company churn).
            </div>
            <div style={{ fontSize: '13px', color: '#555', backgroundColor: '#fff', padding: '8px', borderRadius: '4px', marginBottom: '8px' }}>
              <strong>Formula:</strong> (Sum of Payro user months) ÷ (Sum of min(registered months, months to churn)) × 100
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', lineHeight: '1.6' }}>
              <strong>Key Terms:</strong>
              <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                <li><strong>Payro user months:</strong> Months from first transfer to Payro churn date (if churned) or reference date (if active). Payro churn = no transfer for 3+ months.</li>
                <li><strong>Registered months:</strong> Months from user registration to reference date.</li>
                <li><strong>Months to churn:</strong> Months from registration to company churn date. Company churn = no shifts for 2+ months.</li>
                <li><strong>Denominator:</strong> For each user, uses the lower of registered months or months to churn (if churned).</li>
              </ul>
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
              Example: Company has 10 Payro users. User A: 6 Payro months / 8 registered months. User B: 4 Payro months / 5 registered months (churned after 5 months). User C: 3 Payro months / 3 registered months (still active). Total: 13 Payro months / 16 denominator months = 81.3% retention. This means Payro users remain active for 81.3% of their potential lifetime.
            </div>
          </div>

          {/* Loyalty */}
          <div style={{
            padding: '16px',
            backgroundColor: '#E8F5E9',
            borderRadius: '8px',
            border: '1px solid #81C784',
          }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#2E7D32' }}>
              💎 Loyalty (30% weight)
            </div>
            <div style={{ fontSize: '14px', color: '#424242', lineHeight: '1.6', marginBottom: '8px' }}>
              <strong>Definition:</strong> The percentage of Monthly Active Users (MAU) who made 2 or more transfers in that month.
            </div>
            <div style={{ fontSize: '13px', color: '#555', backgroundColor: '#fff', padding: '8px', borderRadius: '4px' }}>
              <strong>Calculation:</strong> (Repeat users with 2+ transfers) ÷ (Monthly Active Users) × 100
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
              Example: 63 repeat users out of 121 MAU = 52.1% loyalty (58 users made only 1 transfer)
            </div>
          </div>
        </div>

        <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600, color: '#333' }}>
            📊 Table Columns Explained:
          </h4>
          <div style={{ fontSize: '14px', color: '#555', lineHeight: '1.8' }}>
            <div><strong>• Month:</strong> The specific month being measured (e.g., Jan '25, Feb '25)</div>
            <div><strong>• Health Score:</strong> Overall composite score (0-100) with status indicator (🟢🟡🔴)</div>
            <div><strong>• Adoption (35%):</strong> Shows percentage and breakdown (Used Payro / Employees w/ Shifts)</div>
            <div><strong>• Retention (35%):</strong> Shows percentage and number of active employees (total - churned)</div>
            <div><strong>• Loyalty (30%):</strong> Shows percentage and breakdown (Repeat users / MAU)</div>
            <div><strong>• Employees w/ Shifts:</strong> Total count of employees who had at least one shift in that month</div>
            <div><strong>• Used Payro:</strong> Count of employees who made at least one successful transfer in that month</div>
          </div>
        </div>

        <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#E1F5FE', borderRadius: '8px', border: '1px solid #4FC3F7' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: '#0277BD' }}>
            📈 Features:
          </h4>
          <div style={{ fontSize: '14px', color: '#555', lineHeight: '1.8' }}>
            <div><strong>• Health Score Trend Chart:</strong> Visual line graph showing how the health score changes over time for the selected company</div>
            <div><strong>• Monthly Table:</strong> Detailed breakdown of all metrics for each month, showing the components that make up the health score</div>
            <div><strong>• Company Trends Grid:</strong> When viewing "All Companies", see a grid of mini trend charts for each company (click to view details)</div>
          </div>
        </div>
      </div>
    </div>
  );
};
